import React, { useState, useEffect, useMemo } from 'react';


import { Progress, Select, Button, notification } from 'antd';
import axiosInstance from '../../../Services/axiosCommon';
import NuDate from '../../../Components/Utils/NuDate';
import FilesView from '../../../Components/Utils/FilesView';
import EditIcon from '../../../Components/Icons/Edit';
import EditCalendar from '../Inspection/Calender/EditCalendar';
import {
  Building,
  MapPin,
  User,
  CalendarDays,
} from "lucide-react";
import FormikSelectTwo from '../../../Components/Utils/Select';
import { GetAccess, GetUseRole } from '../../../Components/Utils/roles';

const { Option } = Select;

function CalendarIndex({ data, id, getData=()=>{} }) {
  const now = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState(now);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [percentOpenTask, setPercentOpenTask] = useState(false);
  const [newCompletion, setNewCompletion] = useState(0);
  const [filePathList, setFilePathList] = useState([]);

  // Get localized date string (YYYY-MM-DD)
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Months and years for selectors
  const months = useMemo(() => [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ], []);

  const years = useMemo(() => 
    Array.from({ length: 10 }, (_, i) => now.getFullYear() - 5 + i), 
    [now]
  );

  // Fetch calendar data
  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const monthParam = currentDate.getMonth() + 1;
      const yearParam = currentDate.getFullYear();
      const response = await axiosInstance.get(
        `/calendar/events/${monthParam}/month/${yearParam}/year`
      );
      
      const processedData = response.data.tasks_by_date.map(item => ({
        ...item,
        date: getLocalDateString(new Date(item.date))
      }));

      setCalendarData(processedData);

      // Set initial selected date
      const todayString = getLocalDateString(now);
      const todayData = processedData.find(d => d.date === todayString);
      if (todayData) {
        setSelectedDate({
          date: new Date(now),
          ...todayData
        });
        setSelectedTask(todayData.tasks[0]);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  // Calendar grid generation
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month
    const prevMonthLastDate = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDate - i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateString = getLocalDateString(date);
      const dayData = calendarData.find(d => d.date === dateString) || { 
        task_count: 0, 
        tasks: [] 
      };
      days.push({ ...dayData, date, currentMonth: true });
    }

    // Next month (ensure 6 weeks)
    const totalCells = 42;
    const remainingCells = totalCells - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false,
        task_count: 0,
        tasks: [],
      });
    }

    return days;
  }, [calendarData, currentDate]);

  // Date click handler
  const handleDateClick = (day) => {
    setSelectedDate(day);
    if (day.currentMonth && day.task_count > 0) {
      setSelectedTask(day.tasks[0]);
    }
  };

  // Month navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  // Progress circle component
  const renderProgressCircle = (task) => (
    <span 
      className="ml-2 text-gray-700 "
      // onClick={(e) => {
      //   e.stopPropagation();
      //   setSelected(task);
      //   setNewCompletion(task.taskCompletion || 0);
      //   setPercentOpenTask(true);
      // }}
    >
      <Progress
        type="circle"
        size={45}
        strokeWidth={12}
        percent={Math.floor(task.taskCompletion) || 0}
        strokeColor={
          task.taskCompletion > 95 ? "#87d068" :
          task.taskCompletion > 50 ? "#108ee9" : "#D2BD00"
        }
      />
    </span>
  );

  // Update task completion
  const handleUpdateCompletion = async () => {
    if (!selected || newCompletion < 0 || newCompletion > 100) return;
  
    setLoading(true);
    try {
      await axiosInstance.put(`/calendar/task-completion/${selected.id}`, {
        taskCompletion: newCompletion,
      });

      // Update local state
      setCalendarData(prev => prev.map(day => ({
        ...day,
        tasks: day.tasks?.map(task => 
          task.id === selected.id ? { ...task, taskCompletion: newCompletion } : task
        ) || []
      })));

      setPercentOpenTask(false);
      setSelectedTask(prev => prev?.id === selected.id ? 
        { ...prev, taskCompletion: newCompletion } : prev);
        getData();
        fetchCalendarData();
    } catch (error) {
      console.error("Error updating task:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file list updates
  useEffect(() => {
    if (data?.attachments) {
      setFilePathList(Array.isArray(data.attachments) ? 
        data.attachments : 
        JSON.parse(data.attachments));
    }
  }, [data]);


  const [isPopupOpen, setIsPopupOpen] = useState(false);
const [selectedStatus, setSelectedStatus] = useState('');
const [isUpdating, setIsUpdating] = useState(false);
const [api, contextHolder] = notification.useNotification();

const handleSubmit = async () => {
  setIsUpdating(true);
  try {
    const response = await axiosInstance.put(`/calendar/task-status/${id}`, {
      status: selectedStatus,
    });

    if (response.status === 200 || response.data.status === true) {
      api.success({
        message: 'Status updated successfully',
        placement: "bottomRight",
      });
      fetchCalendarData();
    } else {
      api.error({
        message: 'Status update failed',
        description: response.data.msg || "Update unsuccessful",
        placement: "bottomRight",
      });
    }
  } catch (err) {
    api.error({
      message: 'Status update failed',
      description: err.response?.data?.msg || "Network error",
      placement: "bottomRight",
    });
  } finally {
    setIsUpdating(false);
    setIsPopupOpen(false);
  }
};


  return (
    <>
      <div className="md:flex gap-3 mt-0.5 ">
          <div className="bg-white rounded-lg shadow w-full md:w-[60%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-145px)]">
          <div className=" rounded-lg bg-white p-4 lg:border-b lg:flex justify-between items-center h-16">
            <div>
        <h3 className="text-gray-700 text-sm font-bold">Maintenance Calendar</h3>
      <p className="text-gray-500  text-xs font-light">View and manage scheduled maintenance activities</p>

        </div>
        <div className='flex  gap-2 mt-2 lg:mt-0'>
        <Button onClick={() => navigateMonth('prev')} className='border-2  border-baseColor-primary text-baseColor-primary font-bold w-8'>&lt; </Button>

            <div className="flex items-center gap-1">

              <Select
                value={currentDate.getMonth() + 1}
                style={{ width: 100 }}
                onChange={value => {
                  const newDate = new Date(currentDate);
                  newDate.setMonth(value - 1);
                  setCurrentDate(newDate);
                }}
              >
                {months.map(m => (
                  <Option key={m.value} value={m.value}>{m.label}</Option>
                ))}
              </Select>

              <Select
                value={currentDate.getFullYear()}
                style={{ width: 90 }}
                onChange={value => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(value);
                  setCurrentDate(newDate);
                }}
              >
                {years.map(y => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </div>

            <Button onClick={() => navigateMonth('next')} className='border-2  border-baseColor-primary text-baseColor-primary font-bold w-8'> &gt;</Button>
          </div>
          </div>
          <div className="p-4 flex-1 overflow-y-auto min-h-0 mt-9 md:mt-5 lg:mt-0">
            <div className="grid grid-cols-7 w-full h-8  bg-[#6D2323]">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-medium text-white py-1 border border-collapse">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0 w-full  h-[calc(100vh-273px)]">
              {calendarDays.map((day) => {
                const dayDate = new Date(day.date);
                const dateString = getLocalDateString(dayDate);
                const isToday = dateString === getLocalDateString(now);
                const isSelected = selectedDate?.date && 
                  dateString === getLocalDateString(new Date(selectedDate.date));
                const hasTasks = day.task_count > 0;

                return (
                  <div
                    key={dateString}
                    onClick={() => handleDateClick(day)}
                    className={`p-1 border border-collapse h-[calc((100vh-273px)/6)] flex flex-col items-center justify-between
                      ${day.currentMonth ? "" : "bg-gray-50 text-gray-400"}
                      ${isToday ? "bg-yellow-100" : ""}
                      ${isSelected ? "bg-blue-50 border-blue-200" : ""}
                      cursor-pointer hover:bg-blue-50 `}
                  >
                    <div className="w-full text-right text-sm">
                      {dayDate.getDate()}
                    </div>
                    {hasTasks && (
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs 
                        flex items-center justify-center">
                        {day.task_count}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

           <div className="bg-white md:mt-0 mt-2 rounded-lg shadow w-full md:w-[40%] flex flex-col overflow-hidden h-[calc(100vh-84px)] md:h-[calc(100vh-145px)]">
           <div className="w-full p-4 border-b bg-white sticky top-0 z-10">
  <p className="text-[#2A475E] 2xl:text-sm text-xs font-semibold">
    {selectedDate ? 
      `Activity for ${new Date(selectedDate.date).toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })}` : 
      'Select a date'}
  </p>
</div>

          
          <div className="p-4 space-y-4 overflow-y-scroll">
            {selectedDate?.tasks?.length > 0 ? (
              selectedDate.tasks.map(task => (
                <div
                key={task.id}
                className={`bg-white rounded-lg p-2 border  shadow-sm transition
                  ${selectedTask?.id === task.id ? "border-blue-500" : "border-gray-200"}`}
                onClick={() => setSelectedTask(task)}
              >
                  <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xs font-semibold text-gray-800 line-clamp-2">
  {task.description || "No Description"}
</h3>

                      <span className="bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-lg">
                    {task.priority || "None"}

                    </span>
                
                </div>
          
   <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex items-center text-xs gap-2">
                    <Building size={12} className="text-gray-500 shrink-0 mt-0.5" />
                    <span>{task.clientName || "None"}</span>
                  </div>
                  <div className="flex items-center text-xs gap-2">
                    <MapPin size={12} className="text-gray-500 shrink-0 mt-0.5" />
                    <span>{task.location || "None"}</span>
                  </div>
                  <div className="flex items-start text-xs gap-2">
  <User size={12} className="text-gray-500 shrink-0 mt-0.5" />
  <span className="break-words">
    {task.assignees?.map(i => i.assigneeName).join(", ") || ""}
  </span>
</div>

                  <div className="flex items-center text-xs gap-2 justify-between">
                    
                      <div className='flex items-center text-xs gap-2'>
                    <CalendarDays size={12} className="text-gray-500 shrink-0 mt-0.5" />
                    <span>
                      <NuDate value={task.date} format="DD-MMM-YYYY" />
                    </span>
                    </div>
                  

                  </div>
          
                  {/* <p className="text-gray-600 text-sm mt-1">
                    {task.details || "Quarterly inspection of all HVAC units, filter replacement, and system calibration."}
                  </p> */}
                </div>
          
                  <div className="mt-4 flex gap-2 flex-wrap justify-between">
               <div className=" flex items-center  overflow-hidden rounded-full text-[10px] 2xl:text-xs  ">
                            <div className="  p-2 h-6  bg-white rounded-s-full text-xs flex items-center border border-baseColor-primary">
                              Status{" "}
                            </div>
                            <div className="  p-2 h-6  bg-textColor-main text-white text-xs rounded-e-full flex items-center">
                              {task.status || "-"}
                            </div>
                          </div>
                  <div >
                  {/* {(GetUseRole('super admin') || GetUseRole('admin') || GetUseRole('manager')) && (
                  
                       <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  <EditIcon color='blue' height={12} />
                </button>   )   } */}
                {/* <p className="bg-textColor-main text-white p-1 items-center rounded-r-md">
                  {" "}
                  {data?.status || "None"}
                </p> */}
                  {/* <button className="hover:bg-gray-100 border px-3 py-1 text-xs rounded-full" onClick={() => setIsPopupOpen(true)}>Update Status</button> */}
                  {/* <button className="bg-gray-100 px-3 py-1 text-xs cursor-pointer rounded-lg m-0.1">Edit</button> */}
            
                  </div>
                </div>
              </div>
              
              ))
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                Select a date with tasks to view details
              </div>
            )}
          </div>
        </div>
      </div>

      {isPopupOpen && (
  <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 z-[9999] flex justify-center items-center">
    <div className="animate-scale-in w-[90%] max-w-lg bg-white py-6 px-8 rounded-md shadow-lg">
      <h2 className="text-sm font-semibold mb-2">Update Task Status</h2>
      {/* <p className="text-sm text-gray-500 mb-4">
        Change the status of "<strong>HVAC System Inspection</strong>"
      </p> */}

      <div className="space-y-2 mb-6">
        {['Scheduled', 'In Progress', 'Completed', 'Cancelled'].map((status) => (
          <label key={status} className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              value={status}
              checked={selectedStatus === status}
              onChange={() => setSelectedStatus(status)}
              className="accent-baseColor-primary"
            />
            {status}
          </label>
        ))}
      </div>

      <div className="flex justify-end space-x-2">
        <button
          className="px-4 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:text-gray-800"
          onClick={() => setIsPopupOpen(false)}
          disabled={isUpdating}
        >
          Cancel
        </button>
        <button
          className="px-4 py-1 text-sm bg-baseColor-primary text-white rounded-md hover:bg-[#468399] disabled:opacity-50"
          onClick={handleSubmit}
          disabled={isUpdating || !selectedStatus}
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </div>
    </div>
  </div>
)}
        {percentOpenTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Completion</h3>
              <button
                onClick={() => {
                  setPercentOpenTask(false)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <input
              type="number"
              className="w-full p-2 border rounded mb-4"
              value={newCompletion}
              onChange={(e) => {
                const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                setNewCompletion(value);
              }}
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setPercentOpenTask(false)}>Cancel</Button>
              <Button 
                type="primary" 
                onClick={handleUpdateCompletion}
                loading={loading}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      )}

        {isEditModalOpen && (
        <EditCalendar
          open={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          task={selectedTask}
          id={id}
          fetchData={() => {
            getData();
            fetchCalendarData();
          }}
        />
      )}
   </>
  );
}

export default CalendarIndex;